import React, { useState, useEffect, useMemo } from 'react';
import CommunicationHubService from '../../services/CommunicationHubService';
import studentService from '../../services/StudentService';
import { 
  MessageSquare, Send, Settings, History, FileText, ToggleLeft, ToggleRight,
  CheckCircle2, AlertCircle, RefreshCw, Smartphone, Search, Filter, Bell,
  Calendar, Users, BookOpen, CreditCard, Shield, Truck, Award, Sparkles, ChevronRight, Eye
} from 'lucide-react';

const CATEGORIES = [
  { id: 'ALL', label: 'All Categories', icon: Sparkles, color: '#2563eb' },
  { id: 'ATTENDANCE', label: 'Attendance', icon: Bell, color: '#16a34a' },
  { id: 'FEES', label: 'Fees & Payments', icon: CreditCard, color: '#d97706' },
  { id: 'EXAMS', label: 'Examinations', icon: BookOpen, color: '#7c3aed' },
  { id: 'HOMEWORK', label: 'Homework', icon: FileText, color: '#0891b2' },
  { id: 'PTM', label: 'PTM Meetings', icon: Users, color: '#ec4899' },
  { id: 'TRANSPORT', label: 'Transport', icon: Truck, color: '#f97316' },
  { id: 'CERTIFICATES', label: 'Certificates & TC', icon: Award, color: '#059669' },
  { id: 'ANNOUNCEMENTS', label: 'Announcements', icon: MessageSquare, color: '#4f46e5' },
];

const CommunicationCenterStudio = ({ orgData, showNotification }) => {
  const [activeTab, setActiveTab] = useState('compose'); // 'compose', 'templates', 'rules', 'gateway', 'history'
  const [loading, setLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    todayTotal: 0,
    whatsappToday: 0,
    inAppToday: 0,
    activeTemplatesCount: 14,
    activeRulesCount: 8
  });

  // Compose State
  const [composeCategory, setComposeCategory] = useState('ATTENDANCE');
  const [selectedTemplateCode, setSelectedTemplateCode] = useState('');
  const [composeAudience, setComposeAudience] = useState('ALL_STUDENTS'); // 'ALL_STUDENTS', 'CLASS', 'SPECIFIC'
  const [selectedClass, setSelectedClass] = useState('1st Standard');
  const [selectedSection, setSelectedSection] = useState('Section A');
  const [composeMessage, setComposeMessage] = useState('');
  const [channels, setChannels] = useState({ whatsapp: true, sms: false, inApp: true });
  const [sending, setSending] = useState(false);

  // Dynamic parameters for live preview
  const [previewParams, setPreviewParams] = useState({
    studentName: 'Aarav Sharma',
    className: '1st Standard',
    section: 'A',
    rollNo: 'SCH-0001',
    feeAmount: '5,000',
    paidAmount: '3,000',
    outstandingAmount: '2,000',
    dueDate: '31-Aug-2026',
    receiptNo: 'REC-89201',
    examName: 'Term-1 Summative Assessment',
    examDate: '10-Sep-2026',
    marksPercentage: '92.5',
    grade: 'A+',
    subjectName: 'Mathematics',
    homeworkTitle: 'Exercise 4.2 Questions 1-10',
    ptmDate: '05-Sep-2026',
    ptmTime: '10:30 AM',
    busNumber: 'Bus 04',
    routeName: 'North Town Route',
    boardTime: '08:05 AM',
    dropTime: '03:45 PM',
    stopName: 'Main Circle',
    delayMinutes: '15',
    delayReason: 'Traffic Diversion',
    expectedTime: '08:20 AM',
    certificateType: 'Transfer Certificate (TC)',
    announcementTitle: 'Teachers Day Celebration',
    announcementBody: 'School will observe a special morning assembly on 5th September.',
    date: new Date().toISOString().split('T')[0],
    schoolName: orgData?.orgName || orgData?.institutionName || localStorage.getItem('orgName') || 'School Administration'
  });

  // Templates State
  const [templates, setTemplates] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('ALL');
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Rules State
  const [rules, setRules] = useState([]);

  // History State
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [selectedLogModal, setSelectedLogModal] = useState(null);

  // Gateway Config State
  const [gatewayConfig, setGatewayConfig] = useState({
    enableWhatsapp: true,
    enableSms: false,
    enableEmail: false,
    whatsappProvider: 'CUSTOM_GATEWAY',
    whatsappApiUrl: '',
    whatsappApiKey: '',
    whatsappSubUser: '',
    whatsappSenderNumber: '',
    whatsappAbsentTemplate: ''
  });
  const [gatewaySaving, setGatewaySaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Load initial data
  useEffect(() => {
    loadStats();
    loadTemplates();
    loadRules();
    loadGateway();
    loadRealStudentContext();
    if (activeTab === 'history') loadHistory();
  }, [activeTab]);

  const loadRealStudentContext = async () => {
    try {
      const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      const studentList = await studentService.getAllStudents(orgId);
      if (Array.isArray(studentList) && studentList.length > 0) {
        const first = studentList[0];
        setPreviewParams(prev => ({
          ...prev,
          studentName: first.name || first.studentName || 'Student',
          className: first.className || '1st Standard',
          section: first.section || 'A',
          rollNo: first.rollNo || first.studentId || '001',
          schoolName: orgData?.orgName || orgData?.institutionName || 'School Campus'
        }));
      }
    } catch (err) {
      console.warn('Real student preview sync notice:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await CommunicationHubService.getStats();
      if (data) setStats(data);
    } catch (err) {
      console.error('Error loading communication stats:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await CommunicationHubService.getTemplates('ALL');
      if (Array.isArray(data)) {
        setTemplates(data);
        // Default select first template in active compose category
        const initial = data.find(t => t.category === composeCategory);
        if (initial) {
          setSelectedTemplateCode(initial.templateCode);
          setComposeMessage(initial.messageContent);
        }
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadRules = async () => {
    try {
      const data = await CommunicationHubService.getRules('ALL');
      if (Array.isArray(data)) setRules(data);
    } catch (err) {
      console.error('Error loading rules:', err);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await CommunicationHubService.getHistory(historyPage, 25);
      if (data && data.content) {
        setHistoryLogs(data.content);
        setHistoryTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('Error loading history logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGateway = async () => {
    try {
      const data = await CommunicationHubService.getGateway();
      if (data && data.id) {
        setGatewayConfig(prev => ({
          ...prev,
          enableWhatsapp: data.enableWhatsapp ?? true,
          enableSms: data.enableSms ?? false,
          whatsappProvider: data.whatsappProvider || 'CUSTOM_GATEWAY',
          whatsappApiUrl: data.whatsappApiUrl || '',
          whatsappApiKey: data.whatsappApiKey || '',
          whatsappSubUser: data.whatsappSubUser || '',
          whatsappSenderNumber: data.whatsappSenderNumber || '',
          whatsappAbsentTemplate: data.whatsappAbsentTemplate || ''
        }));
      }
    } catch (err) {
      console.error('Error loading gateway config:', err);
    }
  };

  // When compose category changes, auto select corresponding template
  const handleCategoryChange = (catId) => {
    setComposeCategory(catId);
    const catTemplates = templates.filter(t => t.category === catId);
    if (catTemplates.length > 0) {
      setSelectedTemplateCode(catTemplates[0].templateCode);
      setComposeMessage(catTemplates[0].messageContent);
    } else {
      setSelectedTemplateCode('');
      setComposeMessage('');
    }
  };

  const handleTemplateSelection = (code) => {
    setSelectedTemplateCode(code);
    const t = templates.find(item => item.templateCode === code);
    if (t) setComposeMessage(t.messageContent);
  };

  // Live rendered message calculation
  const renderedPreview = useMemo(() => {
    if (!composeMessage) return 'No message content composed yet.';
    let res = composeMessage;
    for (const [k, v] of Object.entries(previewParams)) {
      res = res.replaceAll(`{{${k}}}`, v);
    }
    return res;
  }, [composeMessage, previewParams]);

  // Send Broadcast
  const handleSendBroadcast = async () => {
    if (!composeMessage.trim()) {
      if (showNotification) showNotification('error', 'Message Empty', 'Please enter or select a message to dispatch');
      return;
    }
    if (!channels.whatsapp && !channels.sms && !channels.inApp) {
      if (showNotification) showNotification('error', 'Channel Required', 'Please select at least one delivery channel (WhatsApp, SMS, In-App)');
      return;
    }

    setSending(true);
    try {
      const payload = {
        category: composeCategory,
        templateCode: selectedTemplateCode,
        message: composeMessage,
        audience: composeAudience,
        className: composeAudience === 'CLASS' ? selectedClass : null,
        section: composeAudience === 'CLASS' ? selectedSection : null,
        channels: [
          ...(channels.whatsapp ? ['WHATSAPP'] : []),
          ...(channels.sms ? ['SMS'] : []),
          ...(channels.inApp ? ['IN_APP'] : [])
        ]
      };

      const res = await CommunicationHubService.sendBroadcast(payload);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Broadcast Dispatched', res.message || 'Notifications dispatched successfully');
        loadStats();
      } else {
        if (showNotification) showNotification('error', 'Dispatch Error', res.message || 'Failed to send broadcast');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Dispatch Failed', err.message);
    } finally {
      setSending(false);
    }
  };

  // Save Gateway Settings
  const handleSaveGateway = async () => {
    setGatewaySaving(true);
    try {
      const res = await CommunicationHubService.saveGateway(gatewayConfig);
      if (res && res.id) {
        if (showNotification) showNotification('success', 'Gateway Saved', 'WhatsApp API Gateway and Sub-User credentials saved successfully');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Save Failed', err.message);
    } finally {
      setGatewaySaving(false);
    }
  };

  // Test WhatsApp
  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      if (showNotification) showNotification('error', 'Phone Required', 'Please enter a test mobile number');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await CommunicationHubService.testWhatsApp(
        testPhone.trim(),
        `🔔 [School ERP Test Alert] Connection successful! Your WhatsApp API Gateway and Sub-User (${gatewayConfig.whatsappSubUser || 'Default'}) are properly connected.`
      );
      setTestResult(res);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Test Alert Sent', 'Test WhatsApp message sent successfully!');
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message });
      if (showNotification) showNotification('error', 'Test Failed', err.message);
    } finally {
      setTestLoading(false);
    }
  };

  // Toggle Rule Status
  const handleToggleRule = async (rule) => {
    const updated = { ...rule, enabled: !rule.enabled };
    try {
      await CommunicationHubService.saveRule(updated);
      setRules(prev => prev.map(r => r.id === rule.id ? updated : r));
      if (showNotification) showNotification('success', 'Rule Updated', `${rule.eventTitle} is now ${updated.enabled ? 'Enabled' : 'Disabled'}`);
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    }
  };

  // Save Template Modal
  const handleSaveTemplateModal = async () => {
    if (!editingTemplate) return;
    try {
      const res = await CommunicationHubService.saveTemplate(editingTemplate);
      if (res && res.id) {
        setTemplates(prev => prev.map(t => t.id === res.id ? res : t));
        setEditingTemplate(null);
        if (showNotification) showNotification('success', 'Template Saved', 'Message template updated successfully');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Save Failed', err.message);
    }
  };

  const filteredTemplates = selectedCategoryFilter === 'ALL'
    ? templates
    : templates.filter(t => t.category === selectedCategoryFilter);

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Top Banner & Quick Metrics */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '16px', padding: '24px', color: '#ffffff', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '26px' }}>💬</span>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', letterSpacing: '-0.02em' }}>
                Centralized Communication & Notification Hub
              </h2>
            </div>
            <p style={{ margin: '6px 0 0 0', color: '#94a3b8', fontSize: '13.5px' }}>
              Multi-channel engine for Attendance, Fees, Exams, Homework, PTM, Transport, Certificates & Announcements with WhatsApp Gateway & Sub-User isolation.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 18px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>TODAY SENT</div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#38bdf8' }}>{stats.todayTotal || 0}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 18px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>🟢 WHATSAPP</div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#4ade80' }}>{stats.whatsappToday || 0}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 18px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>🔔 IN-APP</div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#c084fc' }}>{stats.inAppToday || 0}</div>
            </div>
          </div>
        </div>

        {/* Sub Navigation Bar */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', overflowX: 'auto' }}>
          {[
            { id: 'compose', label: '📢 Send & Broadcast', icon: Send },
            { id: 'templates', label: '📝 Message Templates', icon: FileText },
            { id: 'rules', label: '⚙️ Automation Rules', icon: ToggleLeft },
            { id: 'gateway', label: '📱 WhatsApp Gateway & Sub-User', icon: Smartphone },
            { id: 'history', label: '📜 Delivery Reports & Audit', icon: History }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '9px 18px',
                  borderRadius: '10px',
                  border: 'none',
                  background: isActive ? '#2563eb' : 'rgba(255,255,255,0.05)',
                  color: isActive ? '#ffffff' : '#cbd5e1',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ──────────────── TAB 1: SEND & BROADCAST COMPOSER ──────────────── */}
      {activeTab === 'compose' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: '24px' }}>
          
          {/* Left Column: Composer Controls */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 18px 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
              1. Select Notification Category & Event
            </h3>

            {/* Category Pills */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {CATEGORIES.filter(c => c.id !== 'ALL').map(cat => {
                const isSelected = composeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: isSelected ? `2px solid ${cat.color}` : '1px solid #cbd5e1',
                      background: isSelected ? `${cat.color}15` : '#ffffff',
                      color: isSelected ? cat.color : '#475569',
                      fontSize: '12.5px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <cat.icon size={14} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Template Selector */}
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                NOTIFICATION EVENT / TEMPLATE
              </label>
              <select
                value={selectedTemplateCode}
                onChange={e => handleTemplateSelection(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', fontWeight: '600', color: '#0f172a' }}
              >
                {templates.filter(t => t.category === composeCategory).map(t => (
                  <option key={t.templateCode} value={t.templateCode}>{t.templateName}</option>
                ))}
              </select>
            </div>

            {/* Target Audience */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                  TARGET AUDIENCE
                </label>
                <select
                  value={composeAudience}
                  onChange={e => setComposeAudience(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}
                >
                  <option value="ALL_STUDENTS">Entire School (All Parents & Students)</option>
                  <option value="CLASS">Specific Class & Section</option>
                  <option value="SPECIFIC">Custom Student IDs</option>
                </select>
              </div>

              {composeAudience === 'CLASS' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>CLASS</label>
                    <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12.5px' }}>
                      {['1st Standard', '2nd Standard', '3rd Standard', '4th Standard', '5th Standard', '6th Standard', '7th Standard', '8th Standard', '9th Standard', '10th Standard'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ width: '100px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SECTION</label>
                    <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12.5px' }}>
                      {['All', 'Section A', 'Section B', 'Section C'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Delivery Channels */}
            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', marginBottom: '18px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>DELIVERY CHANNELS</div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#16a34a' }}>
                  <input
                    type="checkbox"
                    checked={channels.whatsapp}
                    onChange={e => setChannels({ ...channels, whatsapp: e.target.checked })}
                    style={{ accentColor: '#16a34a' }}
                  />
                  <span>🟢 WhatsApp Gateway</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#2563eb' }}>
                  <input
                    type="checkbox"
                    checked={channels.inApp}
                    onChange={e => setChannels({ ...channels, inApp: e.target.checked })}
                    style={{ accentColor: '#2563eb' }}
                  />
                  <span>🔔 In-App Parent Portal</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>
                  <input
                    type="checkbox"
                    checked={channels.sms}
                    onChange={e => setChannels({ ...channels, sms: e.target.checked })}
                    style={{ accentColor: '#64748b' }}
                  />
                  <span>💬 SMS (Fallback)</span>
                </label>
              </div>
            </div>

            {/* Message Body Editor */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>MESSAGE CONTENT (TEMPLATE EDITABLE)</label>
                <span style={{ fontSize: '11px', color: '#64748b' }}>{composeMessage.length} characters</span>
              </div>
              <textarea
                rows="4"
                value={composeMessage}
                onChange={e => setComposeMessage(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', fontFamily: 'Inter, system-ui', lineHeight: 1.5 }}
              />
            </div>

            {/* Insert Tag Chips */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>INSERT DYNAMIC PLACEHOLDER TAGS:</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  '{{studentName}}', '{{className}}', '{{section}}', '{{rollNo}}',
                  '{{feeAmount}}', '{{paidAmount}}', '{{outstandingAmount}}', '{{dueDate}}',
                  '{{examName}}', '{{examDate}}', '{{homeworkTitle}}', '{{ptmDate}}', '{{busNumber}}',
                  '{{date}}', '{{schoolName}}'
                ].map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setComposeMessage(prev => prev + ' ' + tag)}
                    style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#2563eb', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Send Button */}
            <button
              type="button"
              disabled={sending}
              onClick={handleSendBroadcast}
              style={{
                width: '100%',
                padding: '13px 20px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14.5px',
                fontWeight: '800',
                cursor: sending ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)'
              }}
            >
              <Send size={16} />
              {sending ? 'Dispatching Notifications...' : '🚀 Send Notification to Selected Recipients'}
            </button>
          </div>

          {/* Right Column: Live Mobile WhatsApp Preview */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                📱 Live Personalized Parent Preview
              </h3>
              <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px', background: '#dcfce7', color: '#16a34a' }}>
                WhatsApp View
              </span>
            </div>

            {/* Simulated Phone Frame */}
            <div style={{ background: '#0b141a', borderRadius: '24px', padding: '16px', color: '#e9edef', maxWidth: '360px', margin: '0 auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
              {/* WhatsApp Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '12px', borderBottom: '1px solid #202c33', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#2563eb', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px' }}>
                  S
                </div>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#e9edef' }}>
                    {orgData?.orgName || localStorage.getItem('orgName') || 'School Official'}
                  </div>
                  <div style={{ fontSize: '10.5px', color: '#8696a0' }}>Verified WhatsApp Business Channel</div>
                </div>
              </div>

              {/* Message Bubble */}
              <div style={{ background: '#005c4b', borderRadius: '12px 12px 12px 2px', padding: '12px 14px', fontSize: '13px', lineHeight: 1.5, color: '#e9edef', position: 'relative' }}>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {renderedPreview}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '10px', color: '#8696a0' }}>
                  <span>09:31 AM</span>
                  <span>✓✓</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', padding: '12px 16px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1e40af' }}>
              💡 <strong>Smart Token Replacement:</strong> When sent, the system automatically pulls each student's name, class, pending fee amount, exam dates, or bus delay minutes tailored specifically to that parent.
            </div>
          </div>
        </div>
      )}

      {/* ──────────────── TAB 2: MESSAGE TEMPLATES MANAGER ──────────────── */}
      {activeTab === 'templates' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                Message Template Manager
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                Customize automated WhatsApp, SMS, and In-App templates across all school modules.
              </p>
            </div>

            {/* Category Filter Pills */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryFilter(cat.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: selectedCategoryFilter === cat.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: selectedCategoryFilter === cat.id ? '#eff6ff' : '#ffffff',
                    color: selectedCategoryFilter === cat.id ? '#2563eb' : '#64748b',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Templates Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  background: '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px', background: '#dbeafe', color: '#1e40af' }}>
                      {template.category}
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {template.enableWhatsapp && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700' }}>WhatsApp</span>}
                      {template.enableInApp && <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: '700' }}>In-App</span>}
                    </div>
                  </div>

                  <h4 style={{ margin: '0 0 6px 0', fontSize: '14.5px', fontWeight: '700', color: '#0f172a' }}>
                    {template.templateName}
                  </h4>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px' }}>
                    Code: <code>{template.templateCode}</code>
                  </div>

                  <p style={{ margin: 0, fontSize: '12.5px', color: '#334155', lineHeight: 1.5, background: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', whiteSpace: 'pre-wrap' }}>
                    {template.messageContent}
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                  <button
                    onClick={() => setEditingTemplate({ ...template })}
                    style={{ padding: '6px 14px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    ✏️ Edit Template
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Template Modal */}
          {editingTemplate && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '600px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '800' }}>Edit Message Template</h3>
                
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>TEMPLATE NAME</label>
                  <input
                    type="text"
                    value={editingTemplate.templateName}
                    onChange={e => setEditingTemplate({ ...editingTemplate, templateName: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>MESSAGE CONTENT</label>
                  <textarea
                    rows="4"
                    value={editingTemplate.messageContent}
                    onChange={e => setEditingTemplate({ ...editingTemplate, messageContent: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', lineHeight: 1.5 }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button onClick={() => setEditingTemplate(null)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleSaveTemplateModal} style={{ padding: '8px 20px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>Save Changes</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────────────── TAB 3: AUTOMATION RULES ──────────────── */}
      {activeTab === 'rules' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
              Automation & Notification Trigger Rules
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              Configure when the School ERP automatically fires WhatsApp and in-app alerts based on school events.
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px' }}>Event Name</th>
                  <th style={{ padding: '12px 16px' }}>Category</th>
                  <th style={{ padding: '12px 16px' }}>Trigger Mode</th>
                  <th style={{ padding: '12px 16px' }}>Target</th>
                  <th style={{ padding: '12px 16px' }}>Active Channels</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center' }}>Automation Status</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(rule => (
                  <tr key={rule.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#0f172a' }}>
                      {rule.eventTitle}
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}><code>{rule.eventType}</code></div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '6px', background: '#dbeafe', color: '#1e40af', fontSize: '11px', fontWeight: '700' }}>
                        {rule.category}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#475569' }}>
                      {rule.triggerType}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>
                      {rule.targetAudience || 'PARENTS'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {rule.sendWhatsapp && <span style={{ padding: '2px 6px', borderRadius: '4px', background: '#dcfce7', color: '#16a34a', fontSize: '11px', fontWeight: '700' }}>WhatsApp</span>}
                        {rule.sendInApp && <span style={{ padding: '2px 6px', borderRadius: '4px', background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: '700' }}>In-App</span>}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleRule(rule)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: 'none',
                          background: rule.enabled ? '#dcfce7' : '#fee2e2',
                          color: rule.enabled ? '#15803d' : '#b91c1c',
                          fontWeight: '800',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        {rule.enabled ? '🟢 Active' : '⚪ Disabled'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ──────────────── TAB 4: GATEWAY & SUB-USER CONFIG ──────────────── */}
      {activeTab === 'gateway' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
                📱
              </div>
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  Centralized Communication Infrastructure
                </h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                  Managed and routed centrally by SCMS Central Hub with isolated tenant identity for {orgData?.orgName || 'your institution'}.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '800', padding: '6px 12px', borderRadius: '8px', background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}>
                🟢 Gateway Active (SCMS Hub)
              </span>
            </div>
          </div>

          {/* Centralized Hub Status Banner */}
          <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '14px', padding: '24px', color: '#ffffff', marginBottom: '24px', boxShadow: '0 4px 20px rgba(15, 23, 42, 0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>Communication Gateway Status</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#38bdf8', marginTop: '2px' }}>
                  {orgData?.orgName || 'Institution Official Hub'}
                </div>
                <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '2px' }}>
                  Tenant Org ID: <strong>{orgData?.orgId || 'SCH-02'}</strong> | Mode: <strong>Central Multi-Tenant Infrastructure</strong>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <span style={{ background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>
                  WhatsApp: 🟢 Enabled
                </span>
                <span style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#cbd5e1', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>
                  In-App: 🟢 Active
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Assigned Sub-User / Instance ID</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#f8fafc', marginTop: '2px' }}>
                  <code>{gatewayConfig.whatsappSubUser || `inst_${(orgData?.orgId || 'sch02').toLowerCase().replace('-', '_')}`}</code>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Official Sender Phone</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#f8fafc', marginTop: '2px' }}>
                  {gatewayConfig.whatsappSenderNumber || '+91 98765 43210'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Infrastructure Control</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#10b981', marginTop: '2px' }}>
                  Super Admin Controlled
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Credentials Security</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#94a3b8', marginTop: '2px' }}>
                  ••••••••7A91 (Encrypted)
                </div>
              </div>
            </div>
          </div>

          {/* Test Connection Box */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
              ⚡ Verify Official WhatsApp Delivery
            </h4>
            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 16px 0' }}>
              Send an instant test notification using this school's verified sender channel to confirm real-time parent delivery.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Enter Test Phone Number (e.g. 9876543210)..."
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', width: '280px' }}
              />
              <button
                type="button"
                onClick={handleTestWhatsApp}
                disabled={testLoading}
                style={{ padding: '10px 20px', background: '#16a34a', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: testLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)' }}
              >
                <Smartphone size={16} /> {testLoading ? 'Dispatching Test Alert...' : 'Send Test WhatsApp Message'}
              </button>
            </div>

            {testResult && (
              <div style={{ marginTop: '14px', padding: '10px 16px', borderRadius: '8px', background: testResult.success ? '#dcfce7' : '#fee2e2', color: testResult.success ? '#15803d' : '#b91c1c', fontSize: '13px', fontWeight: '700' }}>
                {testResult.success ? '✅ Verified: Test WhatsApp message delivered successfully!' : `❌ Delivery Failed: ${testResult.error || 'Check gateway connectivity'}`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──────────────── TAB 5: HISTORY & DELIVERY REPORTS ──────────────── */}
      {activeTab === 'history' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                Communication Delivery History & Audit Trail
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                Real-time immutable delivery records of all dispatched WhatsApp and In-App messages.
              </p>
            </div>

            <button
              onClick={loadHistory}
              style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={14} /> Refresh Logs
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>⏳ Loading communication logs...</div>
          ) : historyLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No communication logs recorded yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 14px' }}>Date & Time</th>
                    <th style={{ padding: '10px 14px' }}>Recipient / Student</th>
                    <th style={{ padding: '10px 14px' }}>Category</th>
                    <th style={{ padding: '10px 14px' }}>Channel</th>
                    <th style={{ padding: '10px 14px' }}>Delivery Status</th>
                    <th style={{ padding: '10px 14px' }}>Message Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLogs.map(log => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '12px' }}>
                        {log.sentAt ? new Date(log.sentAt).toLocaleString() : 'Recent'}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: '700', color: '#0f172a' }}>
                        {log.studentName || log.studentId || 'Recipient'}
                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>
                          {log.recipientPhone || log.className || 'General'}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '6px', background: '#dbeafe', color: '#1e40af', fontSize: '11px', fontWeight: '700' }}>
                          {log.category}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: '700', color: log.channel === 'WHATSAPP' ? '#16a34a' : '#2563eb' }}>
                        {log.channel}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '6px', background: log.status === 'DELIVERED' ? '#dcfce7' : log.status === 'FAILED' ? '#fee2e2' : '#f1f5f9', color: log.status === 'DELIVERED' ? '#15803d' : log.status === 'FAILED' ? '#b91c1c' : '#475569', fontSize: '11px', fontWeight: '800' }}>
                          {log.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <button
                          onClick={() => setSelectedLogModal(log)}
                          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', fontWeight: '700', textDecoration: 'underline' }}
                        >
                          View Message
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Details Modal */}
          {selectedLogModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800' }}>Communication Audit Record</h3>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                  Recipient: <strong>{selectedLogModal.recipientPhone || selectedLogModal.studentName}</strong> | Event: <strong>{selectedLogModal.eventType}</strong>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', lineHeight: 1.5, marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                  {selectedLogModal.messageContent}
                </div>

                {selectedLogModal.failureReason && (
                  <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>
                    Failure: {selectedLogModal.failureReason}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setSelectedLogModal(null)} style={{ padding: '8px 18px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default CommunicationCenterStudio;
